import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type DeviceSummary, getDevices } from "../lib/api.ts";
import PushPrompt from "../components/PushPrompt.tsx";

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "active";
  return (
    <span className={`nx-badge ${isActive ? "is-ok" : "is-bad"}`}>
      {status.toUpperCase()}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Devices() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<DeviceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getDevices();
        if (!cancelled) setDevices(res.devices);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="nx-error">{error}</p>;
  if (devices === null) return <p>{t("common.loading")}</p>;
  if (devices.length === 0) return <p>{t("devices.no_devices")}</p>;

  return (
    <div className="nx-devices">
      <p className="nx-label">{t("devices.label")}</p>
      <h1>{t("devices.title")}</h1>

      <PushPrompt />

      <ul className="nx-device-grid">
        {devices.map((d) => (
          <li key={d.mac} className="nx-device-card">
            <Link to={`/devices/${encodeURIComponent(d.mac)}`}>
              <div className="nx-device-card-head">
                <span className="nx-mono">{d.mac}</span>
                <StatusBadge status={d.status} />
              </div>
              <div className="nx-kpi-row">
                <div className="nx-kpi">
                  <span className="nx-kpi-label">{t("devices.soc_bms")}</span>
                  <span className="nx-kpi-value">
                    {d.soc_bms_pct !== null ? `${d.soc_bms_pct}` : "—"}
                    <small>%</small>
                  </span>
                </div>
                <div className="nx-kpi">
                  <span className="nx-kpi-label">{t("devices.voltage")}</span>
                  <span className="nx-kpi-value">
                    {d.voltage_v !== null ? d.voltage_v.toFixed(2) : "—"}
                    <small>V</small>
                  </span>
                </div>
              </div>
              <div className="nx-device-card-foot">
                <span>{t("devices.last_seen")}: {fmtDate(d.last_seen_at)}</span>
                {d.rough_address && <span className="nx-text-secondary">{d.rough_address}</span>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
